/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

export const HEATING_ENERGY_SAVED_ENERGY_WARNING_PERCENTAGE_PER_YEAR = 2.5;
export const HEATING_ENERGY_SAVED_ENERGY_DEFAULT_WARNING_PERCENTAGE = 10;

const MILLISECONDS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

function createLocalDate(
    year: number,
    month: number,
    day: number,
    hours = 0,
    minutes = 0,
    seconds = 0,
): Date | null {
    const date = new Date(year, month - 1, day, hours, minutes, seconds);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        date.getHours() !== hours ||
        date.getMinutes() !== minutes ||
        date.getSeconds() !== seconds
    ) {
        return null;
    }

    return date;
}

function parseLocalDate(value: string | null): Date | null {
    if (!value) {
        return null;
    }

    const normalizedValue = value.trim();
    const dateMatch = normalizedValue.match(
        /^(?:(\d{1,2})\.(\d{1,2})\.(\d{4})|(\d{4})-(\d{2})-(\d{2}))(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
    );

    if (!dateMatch) {
        return null;
    }

    const [, germanDay, germanMonth, germanYear, isoYear, isoMonth, isoDay, hours, minutes, seconds] = dateMatch;

    return createLocalDate(
        Number(germanYear ?? isoYear),
        Number(germanMonth ?? isoMonth),
        Number(germanDay ?? isoDay),
        hours ? Number(hours) : 0,
        minutes ? Number(minutes) : 0,
        seconds ? Number(seconds) : 0,
    );
}

/**
 * Returns the expected saved-energy share since training completion as a percentage.
 */
export function getHeatingEnergySavedEnergyWarningPercentage(
    trainingEnd: string | null,
    latestValueDate: string | null,
): number {
    const trainingEndDate = parseLocalDate(trainingEnd);
    const latestValue = parseLocalDate(latestValueDate);

    if (!trainingEndDate || !latestValue || latestValue <= trainingEndDate) {
        return HEATING_ENERGY_SAVED_ENERGY_DEFAULT_WARNING_PERCENTAGE;
    }

    const elapsedYears = (latestValue.getTime() - trainingEndDate.getTime()) / MILLISECONDS_PER_YEAR;
    const percentage = elapsedYears * HEATING_ENERGY_SAVED_ENERGY_WARNING_PERCENTAGE_PER_YEAR;

    return Math.round(percentage * 10) / 10;
}
